import { BaseAction } from '@cotera/client/v0/actions/framework/action.base';
import type {
  ActionConstructor,
  ActionResult,
  ApplicableContext,
  AskResponse,
  ExecuteContext,
  TargetableModel,
} from '@cotera/client/v0/actions/framework/types';
import { toast } from '@cotera/client/app/components/ui/toaster';
import { COLUMN_ACTION_GROUP } from '@cotera/client/v0/columns/constants';
import { z } from 'zod/v3';
import { Assert, using } from '@cotera/utilities';
import { err, ok } from 'neverthrow';
import type { TenantedClient } from '@cotera/client/app/etc/api/typed-client';
import { GraphModel } from '@cotera/client/v0/graph/graph.model';
import { SetPrimaryKeyAction } from '@cotera/client/v0/dataset/actions/set-pk.action';
import { DatasetModel } from '@cotera/client/v0/dataset/model/dataset.model';
import { ModalPageView } from '@cotera/client/app/components/app/modal-manager/modal-manager';
import { Button } from '@cotera/client/v0/components/ui/button';
import { Icon } from '@cotera/client/app/components/ui/icon';
import { PublishGraphAction } from '@cotera/client/v0/graph/publish.action';
import { v4 } from 'uuid';
import {
  ReadonlyWatchable,
  Watchable,
} from '@cotera/client/v0/actions/framework';
import { EventStreamDefinition } from '@cotera/sdk/core';
import { DisposableModelContext } from '../../actions/etc';

type SnapshotModel = TargetableModel & {
  datasetName: string;
  currentNameInGraph: Watchable<string>;
  isNew: boolean;
  eventStream: ReadonlyWatchable<EventStreamDefinition | null>;
  changes: ReadonlyWatchable<Record<string, boolean | string>>;
  hasErrors: ReadonlyWatchable<boolean>;
};
type ModelConstructor = new (...args: any[]) => SnapshotModel;

export const makeSnapshotAction = (
  title: string,
  modelConstructor: ModelConstructor,
  saveActionConstructor: ActionConstructor<{}, any>
) => {
  return class extends BaseAction<{}, { t: string; message?: string }[]> {
    title = title;
    inputSchema = z.object({});
    group = COLUMN_ACTION_GROUP;
    icon = 'forward';
    discrete = true;

    constructor(
      private readonly client: Pick<
        TenantedClient['warehouse'],
        'snapshotEventStream'
      >,
      private readonly modalManager: {
        pop: () => void;
        push: (props: {
          view: () => React.ReactNode;
          onClose?: () => void;
        }) => void;
      }
    ) {
      super();
    }

    applicable(context: ApplicableContext): boolean {
      const model = getTargetedModel(context, modelConstructor);

      if (model === null || model.hasErrors.snapshot()) {
        return false;
      }

      // Check that eventStream is available (not null)
      // This prevents the action from being applicable when the column
      // hasn't been built yet or the graph is in an invalid state
      const eventStream = model.eventStream.snapshot();
      return eventStream !== null;
    }

    async execute(
      _: {},
      context: ExecuteContext
    ): Promise<ActionResult<{ t: string; message?: string }[]>> {
      const graph = context.getInScopeModelOfType(GraphModel);
      const model = getTargetedModel(context, modelConstructor);

      Assert.assert(model !== null);
      Assert.assert(graph !== null);

      if (model.isNew) {
        const res = await this.askToSave(context);
        if (res.isErr()) {
          return err(res.error);
        }

        // After saving, re-check that eventStream is available before proceeding
        // The save operation may have changed the graph state
        const eventStreamAfterSave = model.eventStream.snapshot();
        if (eventStreamAfterSave === null) {
          return err([
            {
              t: 'eventStreamNotFound',
              message:
                'Event stream not found after saving. The column may not be built yet.',
            },
          ]);
        }

        return await this.askOrExecute(_, context);
      }

      const pk = this.getExistingPk(model, graph);

      if (pk === null) {
        const res = await this.askToSetPk(context, model, graph);
        if (res.isErr()) {
          return err(res.error);
        }

        // After setting PK, re-check that eventStream is available before proceeding
        // The PK operation may have changed the graph state
        const eventStreamAfterPk = model.eventStream.snapshot();
        if (eventStreamAfterPk === null) {
          return err([
            {
              t: 'eventStreamNotFound',
              message:
                'Event stream not found after setting primary key. The column may not be built yet.',
            },
          ]);
        }

        return await this.askOrExecute(_, context);
      }

      if (
        graph.autoPublish.snapshot() &&
        graph.hasUnpublishedOperations.snapshot()
      ) {
        const res = await context
          .getAction(PublishGraphAction)
          ?.askOrExecute({}, context);

        if (res.isErr()) {
          return err(res.error);
        }
      }

      Assert.assert(pk !== null);

      // Re-check eventStream after async operations (publishing, saving, etc.)
      // The graph state may have changed, so we need to verify eventStream is still available
      const eventStream = model.eventStream.snapshot();

      if (eventStream === null) {
        return err([
          {
            t: 'eventStreamNotFound',
            message:
              'Event stream not found. The column may not be built yet or the graph may be in an invalid state.',
          },
        ]);
      }

      const toastHandle = toast.loading(
        `Starting "${model.currentNameInGraph.snapshot()}"...`
      );

      const result = await this.client.snapshotEventStream({
        streamSpecifier: eventStream.specifier,
      });

      if (result.isErr()) {
        toastHandle.error(JSON.stringify(result.error.err));
        return err([
          {
            t: result.error.err.errorType,
          },
        ]);
      }

      toastHandle.success(`${model.currentNameInGraph.snapshot()} started`);
      return ok({});
    }

    shouldAsk(context: ApplicableContext): boolean {
      const graph = context.getInScopeModelOfType(GraphModel);
      const model = getTargetedModel(context, modelConstructor);

      Assert.assert(graph !== null);
      Assert.assert(model !== null);

      // this is handled by the execute function
      if (model.isNew) {
        return false;
      }

      if (this.getExistingPk(model, graph) === null) {
        //this is handled by the execute function
        return false;
      }

      const hasChanges = Object.values(model.changes.snapshot()).some((c) => c);

      return (
        (graph.stagedOperations.snapshot().length > 0 &&
          !graph.autoPublish.snapshot()) ||
        hasChanges
      );
    }

    ask(_: {}, context: ExecuteContext): AskResponse<{}> {
      const model = getTargetedModel(context, modelConstructor);

      Assert.assert(model !== null);

      const hasChanges = Object.values(model.changes.snapshot()).some((c) => c);

      if (hasChanges && !model.isNew) {
        return {
          view: ({ onRespond, onCancel }) => {
            this.modalManager.push({
              view: () => (
                <AskView
                  onRespond={async (params) => {
                    if (!params.existing) {
                      await this.askToSave(context);
                    }
                    await onRespond(params);
                    this.modalManager.pop();
                  }}
                  model={model}
                  context={context}
                />
              ),
              onClose: async () => {
                await onCancel();
              },
            });
          },
        };
      }
      return {
        view: ({ onRespond, onCancel }) => {
          this.modalManager.push({
            view: () => {
              return (
                <UnPublishedAskView
                  onRespond={async (params) => {
                    await onRespond(params);
                    this.modalManager.pop();
                  }}
                  context={context}
                  model={model}
                />
              );
            },
            onClose: async () => {
              await onCancel();
            },
          });
        },
      };
    }

    private getExistingPk(
      model: {
        datasetName: string;
      },
      graph: GraphModel
    ) {
      const relSymbol = graph.graph.snapshot().forSymbol(model.datasetName);
      return (
        relSymbol?.builtNode.match(
          (r) => {
            Assert.assert(r.item.t === 'rel');
            return r.item.pk;
          },
          (_) => null
        ) ?? null
      );
    }

    private async askToSetPk(
      context: ExecuteContext,
      model: {
        datasetName: string;
      },
      graph: GraphModel
    ) {
      const dataset = new DatasetModel(
        {
          name: model.datasetName,
          graph: graph.graph,
          compiler: graph.compiler,
        },
        {
          targeted: true,
          id: model.datasetName + v4(),
        }
      );

      const res = await using(
        new DisposableModelContext(context, dataset),
        async (_) => {
          return await context
            .getAction(SetPrimaryKeyAction)!
            .askOrExecute({}, context);
        }
      );

      if (res.isErr()) {
        return err(res.error);
      }

      if (res.value.cancelled) {
        return err([{ t: 'cancelled' }]);
      }

      return ok(this.getExistingPk(model, graph));
    }

    private async askToSave(context: ExecuteContext) {
      const res = await context
        .getAction(saveActionConstructor)!
        .askOrExecute({}, context);

      if (res.isErr()) {
        return err(res.error);
      }

      if (res.value.cancelled) {
        return err([{ t: 'cancelled' }]);
      }

      return ok(true);
    }
  };
};

const AskView: React.FC<{
  onRespond: (params: { existing?: boolean }) => Promise<void>;
  model: SnapshotModel;
  context: ExecuteContext;
}> = ({ onRespond, model, context }) => {
  return (
    <ModalPageView
      title={`Run ${model.currentNameInGraph.snapshot()}`}
      subtitle={'You have unsaved changes. Do you want to save and run?'}
      className="px-4 py-4"
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={async (e) => {
          e.preventDefault();
        }}
      >
        <div className="flex gap-2 justify-end">
          <Button
            variant="outline"
            onClick={async () => {
              await onRespond({
                existing: true,
              });
            }}
          >
            Run Existing
            <Icon icon="play" size="small" />
          </Button>
          <Button
            variant="default"
            onClick={async () => {
              await context
                .getAction(PublishGraphAction)
                ?.askOrExecute({}, context);
              await onRespond({});
            }}
          >
            Save and Run
            <Icon icon="save" size="small" />
          </Button>
        </div>
      </form>
    </ModalPageView>
  );
};

const UnPublishedAskView: React.FC<{
  onRespond: (params: {}) => Promise<void>;
  model: SnapshotModel;
  context: ExecuteContext;
}> = ({ onRespond, model, context }) => {
  return (
    <ModalPageView
      title={`Run ${model.currentNameInGraph.snapshot()}`}
      subtitle={'You have unpublished changes.'}
      className="px-4 py-4"
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={async (e) => {
          e.preventDefault();
        }}
      >
        <div className="flex gap-2 justify-end">
          {!model.isNew && (
            <Button
              variant="outline"
              onClick={async () => {
                await onRespond({});
              }}
            >
              Run Existing
              <Icon icon="play" size="small" />
            </Button>
          )}
          <Button
            variant="default"
            onClick={async () => {
              await context
                .getAction(PublishGraphAction)
                ?.askOrExecute({}, context);
              await onRespond({});
            }}
          >
            Publish and Run
            <Icon icon="paper-airplane" size="small" />
          </Button>
        </div>
      </form>
    </ModalPageView>
  );
};

const getTargetedModel = (
  context: Pick<ExecuteContext, 'targetedModelOfType'>,
  modelConstructor: ModelConstructor
): SnapshotModel | null => {
  const model = context.targetedModelOfType(modelConstructor);

  return model;
};
